import React, { useContext } from 'react';
import { useLocation } from 'react-router';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { transparentize } from 'polished';
import colors from 'styles/colors';
import SourceContext from 'helpers/SourceContext';
import { SourceType } from 'types/Source';

import Card from '@material-ui/core/Card';
import LinearProgress from '@material-ui/core/LinearProgress';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Page from 'components/Page/newComponents';
import Breadcrumbs from 'components/Page/Breadcrumbs';
import PageTitle from 'components/Page/PageTitle';
import SecurityNotice from 'pages/Directory/Base/SecurityNotice';

import { CHOOSE_SOURCE_TYPE_URL } from 'pages/Directory/consts';
import { SourceContext as SourceContextType } from 'pages/Directory/typings';


const sourceTypeToTotalSteps: { [key in SourceType]?: number } = {
  [SourceType.CSV]: 3,
  [SourceType.AZURE_AD]: 4,
  [SourceType.GOOGLE]: 4,
  [SourceType.WORKDAY]: 5,
};


export interface BaseProps {
  children: React.ReactNode;
  pageTitle?: string;
  description?: string;
  disableNextButton?: boolean;
  footer?: any;
  currentStep?: number;
  buttonText?: string;
  onNext?: (...args: any) => void;
  onBack?: () => void;
  onSkip?: () => void;
}

function Base({
  children,
  currentStep,
  pageTitle,
  description,
  disableNextButton,
  footer,
  buttonText = 'Next',
  onNext,
  onBack,
  onSkip,
}: BaseProps) {
  const classes = useStyles();
  const location = useLocation();
  const sourceContext : SourceContextType = useContext(SourceContext);
  const { sourceInfo } = sourceContext;
  description = sourceInfo.isPrimary
    ? 'Add your people data to Sift by creating a new Directory. A Directory represents a population of people within your organization that can be sourced from a single integration. Let’s get your Directory set up by walking through a few steps.'
    : 'A Complementary Source allows you to append additional data to people within this Directory. Let’s get your Source set up by walking through a few steps.';
  const title = sourceInfo.isPrimary ? 'Create a New Directory' : 'Create a Complementary Source';
  const totalNumberOfSteps = sourceTypeToTotalSteps[sourceInfo.type] || 3;
  const progress = currentStep ? (currentStep / totalNumberOfSteps) * 100 : null;
  const progressText = `Step ${currentStep} of ${totalNumberOfSteps}`;
  const isSourceSelectionPage = location.pathname.includes(CHOOSE_SOURCE_TYPE_URL);

  return (
    <Page>
      <Breadcrumbs routes={['Home', { name: 'Directories', title: 'People Data' }]} />
      <PageTitle title={pageTitle || title} subtitle={description} />
      {progress && (
        <Box className={classes.progressContainer}>
          <Box className={classes.step}>{progressText}</Box>
          <Box className={classes.progress}>
            <LinearProgress
              variant="determinate"
              value={progress}
              className={classes.bar}
              aria-label="progress-bar"
            />
          </Box>
        </Box>
      )}
      {isSourceSelectionPage && <SecurityNotice />}
      <Card className={classes.card}>
        {children}
        <div className={classes.buttonContainer}>
          {onBack && (
            <Button className={classes.backButton} onClick={onBack} variant="outlined">
              Back
            </Button>
          )}
          {onNext && (
            <Button
              data-cy="wizard-next-button"
              className={classes.nextButton}
              onClick={onNext}
              variant="contained"
              color="secondary"
              startIcon={<ArrowForwardIcon />}
              disabled={disableNextButton}
            >
              {buttonText}
            </Button>
          )}
          {onSkip && <Button onClick={onSkip}>Skip</Button>}
        </div>
        {footer && <div className={classes.footer}>{footer}</div>}
      </Card>
    </Page>
  );
}

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 1134,
    width: '100%',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginRight: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  nextButton: {
    minWidth: 86,
    height: 36,
  },
  backButton: {
    width: 67,
    height: 36,
    marginRight: theme.spacing(2),
  },
  footer: {
    marginBottom: theme.spacing(3),
    marginLeft: theme.spacing(3),
  },
  progressContainer: {
    maxWidth: 1134,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(3),
  },
  progress: {
    maxWidth: 300,
    width: '100%',
  },
  bar: {
    maxWidth: 300,
    backgroundColor: transparentize(0.75, colors.brandColors.tertiaryPalette.moss),
    '& .MuiLinearProgress-bar': {
      backgroundColor: colors.brandColors.tertiaryPalette.moss,
    },
  },
  step: {
    marginRight: theme.spacing(2.375),
    maxWidth: 100,
  },
}));

export default Base;
